/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useEffect, useRef} from 'react';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import debounce from 'lodash.debounce';
import isEmpty from 'lodash.isempty';
import {ALIGNED_ROUTES_CONFIG} from '@ion/core/src/services/Here';
import {createCSV} from '@ion/core/src/services/HereUtils';
import {GenericObject, HVSRecord} from '@ion/core/src/types/entities';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {
  HistoryReportTypeEnum,
  HistoryReportTypeFieldEnum
} from '@ion/dls/src/types/types';
import {
  ALL_FEATURES_DEF,
  EXTENT_FACTOR,
  LIVE_HISTORY_EVENT_LINE_LAYER_ID,
  LIVE_HISTORY_FIELDS,
  LiveHistoryEventLineLayerProps,
  mapViewGoToOptions
} from '../constants';
import {
  createAssetsMap,
  createEventLineRenderer,
  createLayerInfoByAsset,
  createLayerInfoByEvent,
  layerExists,
  mergeArrayOrObject,
  splitDataIntoBatches
} from '../helpers/MapViewUtils';
import useZoomTo from '../hooks/useZoomToV2';
import {isLiveHistoryEnabled} from './LayerChooser';

type AddFeaturesProps = {
  graphics: __esri.Graphic[];
  uniqueValues: any[];
  visualVariables: any[];
  deleteFeatures?: __esri.Feature[]
};

enum LiveHistoryLayerAction {
  assetInitialLoad = 'AssetInitialLoad',
  assetUpdates = 'AssetUpdates',
  reportType = 'ReportType'
}

const DATA_BATCH_SIZE = 1000;

const FEATURE_LAYER_ACTION = {
  ADD    : 'addFeatures',
  DELETE : 'deleteFeatures'
};

/**
 * Resolves the color an asset's aligned route is drawn in - its own asset's
 * color, so a page carrying several assets - one filtered by group - draws
 * each asset's line in that asset's color, the same color its events take.
 *
 * ```typescript
 * import {getRouteColor} from '@ion/dls/src/maps/layers/LiveHistoryEventLineLayer';
 *
 * ...
 *
 * const color = getRouteColor(assetsMap[assetId]);
 * ```
 *
 * @param assetEvents - The events of the asset the route belongs to.
 * @returns The color the route is drawn in, `undefined` when there is none.
 */
export const getRouteColor = (assetEvents: GenericObject[] | undefined) => (
  assetEvents?.[0]?.color
);

/**
 * @LiveHistoryEventLineLayer
 *
 * Defines the LiveHistoryEventLineLayer which manages rendering a layer containing the
 * connecting event with a line by asset
 *
 *
 * ```typescript
 * 
 *   import LiveHistoryEventLineLayer from '@ion/dls/src/maps/layers/LiveHistoryEventLineLayer';
 * 
 *   <LiveHistoryEventLineLayer props={...props} />
 * 
 * ```
 *
 */
export const LiveHistoryEventLineLayer = ({
  map,
  view,
  layerProps: {
    assets,
    assetsUpdates,
    layers,
    isActive,
    reportTime,
    reportType,
    inputEvents,
    isHistoryFetching,
    setLiveHistoryUIFetching,
    routes = {},
    routesUpdates = {}
  } = {} as any
}: LiveHistoryEventLineLayerProps) => {
  const historyEventLineLayerRef = useRef(null);
  const historyLayerAction       = useRef(null);
  const assetsRef                = useRef([]);
  const routesRef                = useRef({});
  const pathsRef                 = useRef([]);
  const layersRef                = useRef(layers);
  const {Asset, Event}           = HistoryReportTypeFieldEnum;
  const reportField              = reportType === HistoryReportTypeEnum.Asset ? Asset : Event;
  const color                    = reportField === Event ? dlsColor('transparent') : null;
  const zoomTo                   = useZoomTo();

  const {
    ALIGNED_ROUTES_CSV_HEADERS
  } = ALIGNED_ROUTES_CONFIG;

  const getEventLayerData = reportType === HistoryReportTypeEnum.Asset
    ? createLayerInfoByAsset : createLayerInfoByEvent;

  const isLiveHistoryActive = (layers: string[]) => !layers || isLiveHistoryEnabled(layers);

  const resetState = useCallback(() => {
    assetsRef.current = [];
    routesRef.current = {};
    pathsRef.current  = [];
    historyLayerAction.current = null;
    map.remove(historyEventLineLayerRef.current);
    historyEventLineLayerRef.current = null;
  }, []);

  useEffect(() => {
    if (historyEventLineLayerRef.current) {
      historyEventLineLayerRef.current.visible = isLiveHistoryActive(layers);
    }
    layersRef.current =  layers;
  }, [layers]);

  useEffect(() => {
    displayAssetData();
    return () => {
      resetState();
    };
  }, []);

  useEffect(() => {
    if (isHistoryFetching) {
      resetState();
    }
  }, [isHistoryFetching]);

  useEffect(() => {
    if (assetsUpdates?.length) {
      assetsRef.current = assetsUpdates;
      routesRef.current = routesUpdates;
      pathsRef.current  = mergeArrayOrObject(pathsRef.current, getPaths(assetsUpdates));
      historyLayerAction.current = LiveHistoryLayerAction.assetUpdates;
      displayAssetData();
    }
  }, [assetsUpdates, routesUpdates]);
    
  useEffect(() => {
    if (assets?.length) {
      assetsRef.current = assets;
      routesRef.current = routes;
      pathsRef.current  = mergeArrayOrObject(pathsRef.current, getPaths(assets));
      historyLayerAction.current = LiveHistoryLayerAction.assetInitialLoad;
      displayAssetData();
    }
  }, [assets, routes]);

  useEffect(() => {
    if (historyEventLineLayerRef.current) {
      historyEventLineLayerRef.current.visible = false;
    }
    historyLayerAction.current = LiveHistoryLayerAction.reportType;
    if (reportType === HistoryReportTypeEnum.Asset) {
      removeAllFeature();
    }
    displayAssetData();
  }, [reportType]);

  useEffect(() => {
    if (historyEventLineLayerRef.current) {
      historyEventLineLayerRef.current.visible = false;
    }
    historyLayerAction.current = LiveHistoryLayerAction.reportType;
    displayAssetData();
  }, [inputEvents]);
    
  const getPaths = (assets: HVSRecord[]) => {
    if (assets.length) {
      const assetsMap = createAssetsMap(assets);
      // each asset's route color is read before its events are swapped for
      // the aligned route, which carries no color of its own ...
      const routeColors = {};
      for (const key in assetsMap) {
        routeColors[key]  = getRouteColor(assetsMap[key]);
        const requestBody = createCSV(assetsMap[key], ALIGNED_ROUTES_CSV_HEADERS);
        const result      = routesRef.current[requestBody];
        if (result) {
          assetsMap[key] = result;
        }
      };
      return Object.entries(assetsMap).map(([key, asset]) => {
        const {routes} = asset || {};
        if (routes?.length) {
          const color = routeColors[key];
          return routes.map(route => ({...route, color}));
        }
        return asset;
      }).flat();
    }
  };
    
  const displayAssetData = debounce(async() => {
    if (!assetsRef.current || assetsRef.current?.length <= 0 || !isActive) {
      const timeoutId = setTimeout(() => {
        clearTimeout(timeoutId);
        removeAllFeature();
        map.remove(historyEventLineLayerRef.current);
        historyEventLineLayerRef.current = null;
        setLiveHistoryUIFetching(false);
      }, 50);
      return false;
    }

    const {
      graphics,
      uniqueValues,
      visualVariables
    } = await getEventLayerData(pathsRef.current, inputEvents, Number(reportTime));

    if (!historyEventLineLayerRef.current) {
      if (!layerExists(map, LIVE_HISTORY_EVENT_LINE_LAYER_ID) && !isEmpty(uniqueValues)) {
        historyEventLineLayerRef.current = new FeatureLayer({
          title         : LIVE_HISTORY_EVENT_LINE_LAYER_ID,
          id            : LIVE_HISTORY_EVENT_LINE_LAYER_ID,
          source        : [graphics[0]],
          objectIdField : 'OBJECTID',
          fields        : LIVE_HISTORY_FIELDS as any,
          renderer      : createEventLineRenderer({uniqueValues, visualVariables, reportField, color})
        });
        removeAllFeature().then(() => {
          applyEditsToLayer({addFeatures: graphics});
        });
        map.layers.add(historyEventLineLayerRef.current);
        map.layers.reorder(historyEventLineLayerRef.current, 1);
        historyEventLineLayerRef.current.visible = isLiveHistoryActive(layers);
        view.whenLayerView(historyEventLineLayerRef.current).then(layerView => {
          layerView.watch('updating', updating => {
            if (!updating) {
              setLiveHistoryUIFetching(false);
            }
          });
        });
      }
    } else {
      updateHistoryLayer({graphics, uniqueValues, visualVariables});
    }
    historyLayerAction.current = null;
  }, [30]);

  const updateHistoryLayer = async({graphics, uniqueValues, visualVariables}: AddFeaturesProps) => {
    if (reportField === HistoryReportTypeFieldEnum.Event &&
      historyLayerAction.current === LiveHistoryLayerAction.reportType) {
      const {features} = await historyEventLineLayerRef.current.queryFeatures(ALL_FEATURES_DEF);
      addFeatures({graphics, uniqueValues, visualVariables, deleteFeatures: features});
    } else {
      let newGraphics = graphics;
      if (historyLayerAction.current !== LiveHistoryLayerAction.reportType) {
        const {graphics} = await getEventLayerData(
          getPaths(assetsRef.current),
          inputEvents,
          Number(reportTime)
        );
        newGraphics = graphics;
      }
      addFeatures({graphics: newGraphics, uniqueValues, visualVariables, deleteFeatures: []});
    }
  };
    
  const addFeatures = debounce(async({graphics, uniqueValues, visualVariables, deleteFeatures}: AddFeaturesProps) => {
    if (historyEventLineLayerRef.current) {
      const renderer = createEventLineRenderer({uniqueValues, visualVariables, reportField, color});
      if (!isEmpty(renderer)) {
        historyEventLineLayerRef.current.renderer = renderer;
        historyEventLineLayerRef.current.refresh();
      }
      const addBatches    = splitDataIntoBatches(graphics, DATA_BATCH_SIZE);
      const deleteBatches = splitDataIntoBatches(deleteFeatures, DATA_BATCH_SIZE);

      await applyEditsInBatches(FEATURE_LAYER_ACTION.ADD, addBatches);
      await applyEditsInBatches(FEATURE_LAYER_ACTION.DELETE, deleteBatches);
    }
  }, [300]);
    
  const removeAllFeature = async() => {
    return historyEventLineLayerRef?.current?.queryFeatures().then(function({features}) {
      applyEditsToLayer({deleteFeatures: features});
    });
  };
    
  const applyEditsToLayer = (edits) => {
    if (historyEventLineLayerRef.current) {
      historyEventLineLayerRef.current.applyEdits(edits);
    }
  };
    
  const zoomToFeatures = (assets) => {
    const extent = zoomTo(addGeometry(assets), EXTENT_FACTOR);
    extent && view.goTo({geometry: extent}, mapViewGoToOptions);
  };

  const addGeometry = (assets) => {
    return assets?.map((item) => {
      return {
        ...item,
        geometry : {
          type        : 'Point',
          coordinates : [item.longitude, item.latitude]
        }
      };
    });
  };
  
  /*
  * Applies edits to the FeatureLayer in small batches to improve UI responsiveness
  * and avoid blocking the main thread during large data operations. The layer is
  * captured once rather than re-read off the ref after every batch's await - a
  * fetch running the same time this is can tear it down mid-loop (`resetState`
  * on `isHistoryFetching`), and a stale layer is never written to rather than
  * throwing against a null ref.
  * **/
  const applyEditsInBatches = async(operation: string, data: HVSRecord[]) => {
    const layer = historyEventLineLayerRef.current;
    if (!layer) {
      return;
    }
    for (const batch of data) {
      await layer.applyEdits({[operation]: batch});
      if (historyEventLineLayerRef.current !== layer) {
        return;
      }
      await new Promise(resolve => setTimeout(resolve, 50)); // Allow UI to update
    }
    layer.visible = isLiveHistoryActive(layersRef.current);
  };
  
  return null;
};

export default LiveHistoryEventLineLayer;
